import { FC } from 'react';
import { toast } from 'react-toastify';
import { ArrowForwardIos } from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';

import { TapToEjectIcon } from '~assets/icons';
import { PAYMENT_STEP } from '~common';
import { FormattedEndAt } from '~components/atoms/new-step-three/FormattedEndAt';
import { PassNoticeBanner } from '~components/atoms/pass/PassNoticeBanner';
import { LocalizationTypography } from '~components/organisms/localization';
import { useRentCharger } from '~hooks/payment/useRentCharger';
import { usePaymentStepContext, useUserSubscriptionContext } from '~providers';
import { isSwapLimitReached } from '~utils/pass.util';

export const AvailableSwapStep: FC = () => {
  const { activeSubscription, activePass, isPassCanceled } =
    useUserSubscriptionContext();
  const { handleSetPaymentStep } = usePaymentStepContext();
  const { handleRentCharger, isRentingCharger } = useRentCharger();

  if (!activeSubscription) {
    return null;
  }

  const swapsLeft = activeSubscription.swapsLeft;
  const usedChargers = activeSubscription.usedChargers;
  const limitReached = isSwapLimitReached(activeSubscription);
  const isEjectDisabled = isRentingCharger || limitReached;

  const handleTapToEject = () => {
    if (limitReached) {
      return;
    }

    if (isRentingCharger) {
      toast.error('We are currently processing your rental request');
      return;
    }

    handleRentCharger(
      undefined,
      undefined,
      activeSubscription.userSubscriptionId,
    );
  };

  return (
    <Stack
      sx={{
        paddingBottom: 1.5,
        alignItems: 'center',
        textAlign: 'center',
        gap: 3,
      }}
    >
      <LocalizationTypography
        textId="app.rentalPage.newStepThree.swapCharger.welcome"
        defaultText="Welcome back!"
        variant="Poppins/Heading-3"
      />

      {/* A cancelled pass keeps working to the end of the paid period —
          returning the last charger no longer ends it early. */}
      {isPassCanceled && activeSubscription.endAt && (
        <PassNoticeBanner tone="info">
          <LocalizationTypography
            textId="app.rentalPage.newStepThree.pass.canceledAccessUntil"
            defaultText="Your pass is cancelled. You keep access until the end of this period."
            variant="Poppins/Body-sm"
            color="inherit"
          />
        </PassNoticeBanner>
      )}

      <Stack gap={0.5}>
        {swapsLeft !== -1 ? (
          <>
            <LocalizationTypography
              textId="app.rentalPage.newStepThree.swapCharger.swapsLeftLabel"
              defaultText="Swaps left"
              variant="Poppins/Body-sm"
              color="#6B7280"
              fontWeight={500}
            />
            <LocalizationTypography
              textId="app.rentalPage.newStepThree.swapCharger.swaps"
              defaultText="{{count}} Chargers"
              textValues={{ count: swapsLeft }}
              variant="Poppins/Subheading-md"
              color="#111928"
            />
          </>
        ) : (
          <>
            <LocalizationTypography
              textId="app.rentalPage.newStepThree.swapCharger.usedLabel"
              defaultText="Used"
              variant="Poppins/Body-sm"
              color="#6B7280"
              fontWeight={500}
            />
            <LocalizationTypography
              textId="app.rentalPage.newStepThree.swapCharger.swaps"
              defaultText="{{count}} Chargers"
              textValues={{ count: usedChargers }}
              variant="Poppins/Subheading-md"
              color="#111928"
            />
          </>
        )}
      </Stack>

      {activeSubscription?.endAt && (
        <Stack gap={0.5}>
          <LocalizationTypography
            textId="app.rentalPage.newStepThree.swapCharger.validUntilLabel"
            defaultText="Valid until"
            variant="Poppins/Body-sm"
            color="#6B7280"
            fontWeight={500}
          />
          <Typography variant="Poppins/Subheading-md" color="#111928">
            <FormattedEndAt endAt={activeSubscription.endAt} />
          </Typography>
        </Stack>
      )}

      {/* Neither a cancelled nor a one-off pass has a next billing date, so
          neither may promise a reset. `activePass` is null for a legacy period,
          which keeps the old wording. */}
      {limitReached && (
        <PassNoticeBanner>
          {isPassCanceled || activePass?.autoRenew === false ? (
            <LocalizationTypography
              textId="app.rentalPage.newStepThree.pass.limitReachedNoRenewal"
              defaultText="You’ve used all your swaps for this period."
              variant="Poppins/Body-md"
              color="inherit"
            />
          ) : (
            <LocalizationTypography
              textId="app.rentalPage.newStepThree.pass.limitReached"
              defaultText="You’ve used all your swaps for this cycle. Your swaps will reset on your next billing date."
              variant="Poppins/Body-md"
              color="inherit"
            />
          )}
        </PassNoticeBanner>
      )}

      <Stack gap={1} alignItems="center">
        <LocalizationTypography
          textId="app.rentalPage.newStepThree.swapCharger.tapToEject"
          defaultText="Tap to eject"
          variant="Poppins/Subheading-md"
          sx={{ color: 'brand.dark', textTransform: 'uppercase' }}
        />

        <Box
          onClick={handleTapToEject}
          sx={{
            width: 160,
            height: 160,
            borderRadius: '50%',
            backgroundColor: 'brand.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: isEjectDisabled ? 'not-allowed' : 'pointer',
            pointerEvents: isEjectDisabled ? 'none' : 'auto',
            opacity: isEjectDisabled ? 0.5 : 1,
            transition:
              'background-color 0.2s ease, transform 0.1s ease, box-shadow 0.2s ease',
            boxShadow: '0 10px 20px rgba(0, 0, 0, 0.15)',
            '&:hover': {
              backgroundColor: '#00B4FF',
              transform: 'translateY(-2px) scale(1.03)',
              boxShadow: '0 14px 28px rgba(0, 0, 0, 0.18)',
            },
            '&:active': {
              backgroundColor: '#0080CF',
              transform: 'translateY(0) scale(0.97)',
              boxShadow: '0 6px 12px rgba(0, 0, 0, 0.2)',
            },
          }}
        >
          <Box
            sx={{
              width: 150,
              height: 150,
              borderRadius: '50%',
              border: '2px solid #F9FAFB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isRentingCharger ? (
              <CircularProgress size={50} />
            ) : (
              <TapToEjectIcon />
            )}
          </Box>
        </Box>
      </Stack>

      {/* A legacy period belongs to no pass, so there is nothing to open. */}
      {activePass && (
        <ButtonBase
          onClick={() => handleSetPaymentStep(PAYMENT_STEP.PASS_DETAIL)}
          sx={{
            width: '100%',
            bgcolor: 'brand.lightest',
            border: '1px solid #CCEDFF',
            borderRadius: '24px',
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <LocalizationTypography
            textId="app.rentalPage.newStepThree.pass.managePass"
            defaultText="Manage my pass"
            variant="Poppins/Subheading-md"
            sx={{ color: 'brand.darkest', flex: 1, textAlign: 'left' }}
          />
          <ArrowForwardIos sx={{ color: 'brand.main', fontSize: '14px' }} />
        </ButtonBase>
      )}
    </Stack>
  );
};
